/**
 * Song work of the new-chart wizard (W2-14 onboarding-wizard; DESIGN.md §5.1 step 2, P§2.7):
 *  - sniffing (codec, "Phira can play this") with the pure formats sniffer, which reads only the headers;
 *  - SHA-256 through the io worker (the asset id and the analysis-cache key), crypto.subtle when the worker fails;
 *  - decoding with Web Audio (the app's AudioContext, or an OfflineAudioContext when the engine has none yet);
 *  - handing the PCM to the audio worker (`dsp.setPcm`) and the background tempo / first-beat detection whose result
 *    goes into `NewChartDraft.analysis` for the Beat step (cached per song SHA-256 in the persist analysis cache);
 *  - 16-bit WAV conversion for formats Phira cannot play (M4A, Opus) and, optionally, for FLAC (RPE cannot open it):
 *    the audio worker's `encodeWav`, or the same encoder here when the worker fails;
 *  - the waveform overview (min / max per column of the mono mix).
 */

import type { AppServices, NewChartDraft } from '../../app/registry/types';
import { copyChannels, decodeAudio, type DecodeContext } from '../../audio/decode';
import { encodeWav } from '../../core/audio-dsp/wav';
import type { TempoCandidate } from '../../core/audio-dsp/types';
import type { AssetId, AssetMeta } from '../../core/model/types';
import { assetMetaOf, sniffMedia } from '../../formats/package/sniff';
import type { MediaSniff } from '../../formats/types';

/** Decoded song kept by the wizard while it runs (never part of the draft: the draft holds the file bytes). */
export interface DecodedSong {
  readonly channels: readonly Float32Array[];
  readonly sampleRate: number;
  readonly durationSec: number;
}

/** Tempo range searched by the background detection (DESIGN.md §5.2: 60-240 BPM). */
export const DETECT_MIN_BPM = 60;
export const DETECT_MAX_BPM = 240;
/** Sample rate of the fallback decoding context. */
const FALLBACK_SAMPLE_RATE = 48000;

export type SongVerdict =
  /** Phira and RPE both play it (OGG Vorbis, WAV). */
  | 'ok'
  /** Phira plays it; MP3 decoder delay differs between players (soft note, ST-07). */
  | 'mp3'
  /** Phira plays it; RPE cannot open it (FLAC, AAC): WAV copy offered for RPE (ST-08). */
  | 'notForRpe'
  /** Phira cannot play it (M4A / AAC in MP4, Opus, ...): conversion to WAV needed. */
  | 'needsConversion';

export function songVerdict(sniff: Pick<MediaSniff, 'codec' | 'phiraDecodable' | 'rpeFriendly'>): SongVerdict {
  if (!sniff.phiraDecodable) return 'needsConversion';
  if (sniff.codec === 'mp3') return 'mp3';
  if (!sniff.rpeFriendly) return 'notForRpe';
  return 'ok';
}

/** Sniffs a file's content (the name only breaks ties). */
export function sniffFile(bytes: Uint8Array, name: string): MediaSniff {
  return sniffMedia(bytes, name);
}

/** Asset metadata of a file (codec, Phira decodability, duration / size from the container headers). */
export function metaOf(bytes: Uint8Array, name: string): AssetMeta {
  return assetMetaOf(bytes, sniffMedia(bytes, name));
}

/** Lowercase hex SHA-256 with crypto.subtle (fallback when the io worker is unavailable). */
async function sha256Local(bytes: Uint8Array): Promise<string> {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const digest = await crypto.subtle.digest('SHA-256', copy);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** SHA-256 of a file (asset id): io worker first, crypto.subtle when the worker fails. */
export async function sha256Of(services: Pick<AppServices, 'io'>, bytes: Uint8Array): Promise<AssetId> {
  try {
    const hex = await services.io.call('sha256', { bytes });
    if (/^[0-9a-f]{64}$/.test(hex)) return hex as AssetId;
  } catch {
    // The worker is unavailable: hash here.
  }
  return (await sha256Local(bytes)) as AssetId;
}

/** The context used for decoding: the engine's (created on demand), else an offline one. */
function decodeContext(services: Pick<AppServices, 'audio'>): DecodeContext {
  const ctx = services.audio.context;
  if (ctx !== null) return ctx;
  if (typeof OfflineAudioContext === 'function') {
    return new OfflineAudioContext({ numberOfChannels: 2, length: 1, sampleRate: FALLBACK_SAMPLE_RATE });
  }
  throw new Error('Web Audio is not available');
}

/**
 * Starts the app's AudioContext from the current user gesture without waiting for it (decoding works on a suspended
 * context; playback resumes it later). Call it synchronously inside the click / drop / change handler.
 */
export function wakeAudio(services: Pick<AppServices, 'audio'>): void {
  try {
    services.audio.ensureContext().catch(() => undefined);
  } catch {
    // No Web Audio: decoding falls back to an offline context.
  }
}

/** Decodes a song (throws when the browser cannot decode it). */
export async function decodeSong(services: Pick<AppServices, 'audio'>, bytes: Uint8Array): Promise<DecodedSong> {
  const buffer = await decodeAudio(decodeContext(services), bytes);
  return { channels: copyChannels(buffer), sampleRate: buffer.sampleRate, durationSec: buffer.duration };
}

/** Fresh copies of the channels (safe to transfer to a worker). */
function channelCopies(song: DecodedSong): Float32Array[] {
  return song.channels.map((c) => {
    const copy = new Float32Array(c.length);
    copy.set(c);
    return copy;
  });
}

/** Installs the PCM in the audio worker under the song's SHA-256 (the Beat step's analysis key). */
export async function installPcm(
  services: Pick<AppServices, 'dsp'>,
  audio: AssetId,
  song: DecodedSong,
): Promise<boolean> {
  const channels = channelCopies(song);
  try {
    await services.dsp.call(
      'setPcm',
      { audio, channels, sampleRate: song.sampleRate },
      channels.map((c) => c.buffer),
    );
    return true;
  } catch {
    return false;
  }
}

function validCandidates(list: unknown): readonly TempoCandidate[] | null {
  if (!Array.isArray(list)) return null;
  const out = (list as readonly TempoCandidate[]).filter(
    (c) => Number.isFinite(c.bpm) && c.bpm > 0 && Number.isFinite(c.strength),
  );
  return out.length > 0 ? out : null;
}

/**
 * Background tempo and first-beat detection (DESIGN.md §5.1: "Tempo detection starts in the background now").
 * Tempo candidates come from the persist analysis cache when this song was analysed before. Never throws: a failing
 * worker gives `candidates: null` and the Beat step offers detection again.
 */
export async function detectTiming(
  services: Pick<AppServices, 'dsp' | 'persist'>,
  audio: AssetId,
): Promise<NonNullable<NewChartDraft['analysis']>> {
  let candidates = validCandidates(await services.persist.analysisGet(audio, 'tempo').catch(() => undefined));
  if (candidates === null) {
    try {
      candidates = validCandidates(
        await services.dsp.call('tempoCandidates', { audio, minBpm: DETECT_MIN_BPM, maxBpm: DETECT_MAX_BPM }),
      );
      if (candidates !== null) await services.persist.analysisSet(audio, 'tempo', candidates).catch(() => undefined);
    } catch {
      candidates = null;
    }
  }
  const best = candidates === null ? null : [...candidates].sort((a, b) => b.strength - a.strength)[0];
  let offset: { readonly offsetMs: number; readonly confidence: number } | null = null;
  if (best !== undefined && best !== null) {
    try {
      const r = await services.dsp.call('suggestOffset', {
        audio,
        bpm: [{ beat: 0, bpm: best.bpm }],
        currentOffsetMs: 0,
      });
      if (Number.isFinite(r.offsetMs) && Number.isFinite(r.confidence)) offset = r;
    } catch {
      offset = null;
    }
  }
  return { candidates, offset };
}

/** 16-bit WAV of the decoded song: the audio worker's encoder, else the same encoder on this thread. */
export async function convertToWav(services: Pick<AppServices, 'dsp'>, song: DecodedSong): Promise<Uint8Array> {
  const channels = channelCopies(song);
  try {
    const bytes = await services.dsp.call(
      'encodeWav',
      { channels, sampleRate: song.sampleRate },
      channels.map((c) => c.buffer),
    );
    if (bytes instanceof Uint8Array && bytes.length > 44) return bytes;
  } catch {
    // The worker is unavailable: encode here.
  }
  return encodeWav(song.channels, song.sampleRate, 16);
}

/**
 * Waveform overview: for each of `columns` columns, the min and max of the mono mix (interleaved [min0, max0,
 * min1, max1, ...], values in -1..1).
 */
export function waveformPeaks(song: DecodedSong, columns: number): Float32Array {
  const n = Math.max(1, Math.floor(columns));
  const out = new Float32Array(n * 2);
  const length = song.channels[0]?.length ?? 0;
  if (length === 0) return out;
  const channels = song.channels;
  const k = channels.length;
  for (let c = 0; c < n; c++) {
    const from = Math.floor((c * length) / n);
    const to = Math.max(from + 1, Math.floor(((c + 1) * length) / n));
    let lo = Infinity;
    let hi = -Infinity;
    // Long blocks are sampled with a stride (an overview needs no more than ~2,000 reads per column).
    const step = Math.max(1, Math.floor((to - from) / 2000));
    for (let i = from; i < to; i += step) {
      let v = 0;
      for (const ch of channels) v += ch[i] ?? 0;
      v /= k;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    out[c * 2] = Number.isFinite(lo) ? lo : 0;
    out[c * 2 + 1] = Number.isFinite(hi) ? hi : 0;
  }
  return out;
}
